// Aerodynamics with 2026 active aero: Corner Mode (high downforce) and
// Straight Mode (low drag), switching in at most 400 ms (C3.10.10 o, C3.11.6 d).
// Coefficients are estimates tuned to FIA targets (no regulation figure exists):
// roughly -30% downforce vs 2025 in Corner Mode, and a large drag cut in Straight Mode.

import { param } from '../config/params';

export const aeroParams = {
  clACorner: param({ id: 'aero.clACorner', label: 'ClA corner (m²)', group: 'Aero', min: 1, max: 7, step: 0.05, value: 5.0 }),
  cdACorner: param({ id: 'aero.cdACorner', label: 'CdA corner (m²)', group: 'Aero', min: 0.5, max: 2, step: 0.01, value: 1.0 }),
  clAStraight: param({ id: 'aero.clAStraight', label: 'ClA straight (m²)', group: 'Aero', min: 0.5, max: 5, step: 0.05, value: 2.4 }),
  cdAStraight: param({ id: 'aero.cdAStraight', label: 'CdA straight (m²)', group: 'Aero', min: 0.4, max: 1.5, step: 0.01, value: 0.7 }),
  balanceFront: param({ id: 'aero.balanceFront', label: 'Aero balance front', group: 'Aero', min: 0.3, max: 0.55, step: 0.01, value: 0.445 }),
};

export const AIR_DENSITY = 1.18;
export const AERO_TRANSITION_S = 0.4;
/** Share of the full Straight Mode effect with only the front wing open (estimate). */
export const PARTIAL_BLEND = 0.45;

export type AeroMode = 'corner' | 'straight';

export class ActiveAero {
  /** 0 = Corner Mode, 1 = Straight Mode, blended over the transition time. */
  blend = 0;
  mode: AeroMode = 'corner';

  /**
   * Automatic activation (game assist): open at full throttle inside an
   * activation zone, close on braking, lifting or leaving the zone.
   */
  update(inZone: boolean, throttle: number, brake: number, dt: number, partial = false): void {
    // A small threshold: an analog brake pedal (or a resting foot) never reads exactly 0.
    const wantOpen = inZone && throttle > 0.9 && brake < 0.03;
    this.mode = wantOpen ? 'straight' : 'corner';
    // Low Grip Conditions: front wing only, roughly half the drag/downforce change.
    const target = wantOpen ? (partial ? PARTIAL_BLEND : 1) : 0;
    const step = dt / AERO_TRANSITION_S;
    this.blend += Math.max(-step, Math.min(step, target - this.blend));
  }

  get clA(): number {
    return lerp(aeroParams.clACorner.value, aeroParams.clAStraight.value, this.blend);
  }

  get cdA(): number {
    return lerp(aeroParams.cdACorner.value, aeroParams.cdAStraight.value, this.blend);
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
